'use client'

import { ReactElement, startTransition } from 'react'

import Image from 'next/image'
import Script from 'next/script'

import clsx from 'clsx'
import dayjs from 'dayjs'
import { useLocale, useTranslations } from 'next-intl'

import ArticleBlockItem from '@/components/site/ArticleBlockItem'
import ArticleGoogleSchema from '@/components/site/ArticleGoogleSchema'
import Breadcrumbs from '@/components/site/Breadcrumbs'
import SurveyBanner from '@/components/site/SurveyBanner'
import ToTelegramBanner from '@/components/site/ToTelegramBanner'
import {
    CONTENT_LOCALE_CONFIG,
    getDayjsLocale,
    getIntlLocale,
    type ContentLocale,
} from '@/i18n/contentLocales'
import { getLocalizedUrl } from '@/i18n/localeUrls'
import { useRouter } from '@/i18n/navigation'
import { extractFaq } from '@/lib/blogFaq'
import { generateImagePlaceHolder } from '@/lib/utils'
import { guardOutputData } from '@/types/guards'

import styles from './styles.module.scss'

import type { PublishedTranslation } from '@/services/blog.service'

import 'dayjs/locale/ru'
import 'dayjs/locale/en'

interface Props {
    article: PublishedTranslation
    localePaths: Partial<Record<ContentLocale, string>>
}

export default function Article({ article, localePaths }: Props): ReactElement {
    const t = useTranslations('blog')
    const tCommon = useTranslations('common')
    const locale = useLocale() as ContentLocale
    const router = useRouter()

    dayjs.locale(getDayjsLocale(locale))

    const coverImage = article.blog.coverImage ?? ''
    const publishedAt =
        article.publishedAt?.toISOString() ??
        article.blog.createdAt.toISOString()
    const articleUrl = getLocalizedUrl(locale, `/blog/${article.slug}`)

    // Blog language switching is hidden for now.
    const switcherLocales: ContentLocale[] = []

    return (
        <>
            <Script
                src={'https://yastatic.net/share2/share.js'}
                onReady={() => window.Ya.share2('ya-share2')}
            />
            <div className={styles.wrapper}>
                <Breadcrumbs
                    items={[
                        {
                            title: tCommon('home'),
                            href: '/',
                        },
                        {
                            title: t('title'),
                            href: '/blog',
                        },
                        {
                            title: article.title,
                        },
                    ]}
                />
                <SurveyBanner />
                <article className={styles.outer}>
                    <header className={styles.header}>
                        <div className={styles.header__row}>
                            <div className={styles.header__nav}>
                                <div className={styles.views}>
                                    {(
                                        article.blog.views +
                                        article.blog.additionalViews
                                    ).toLocaleString(getIntlLocale(locale))}
                                </div>
                                {switcherLocales.length > 1 ? (
                                    <div
                                        className={styles.localeSwitcher}
                                        role={'navigation'}
                                        aria-label={t('languageVersions')}
                                    >
                                        {switcherLocales.map(code => (
                                            <button
                                                key={code}
                                                type={'button'}
                                                className={
                                                    styles.localeSwitcher__btn
                                                }
                                                data-active={code === locale}
                                                onClick={() => {
                                                    const path =
                                                        localePaths[code]
                                                    if (!path) return
                                                    startTransition(() => {
                                                        router.push(path, {
                                                            locale: code,
                                                        })
                                                    })
                                                }}
                                            >
                                                {
                                                    CONTENT_LOCALE_CONFIG[code]
                                                        .label
                                                }
                                            </button>
                                        ))}
                                    </div>
                                ) : null}
                                <div
                                    id={'ya-share2'}
                                    className={clsx('ya-share2', styles.share)}
                                    data-curtain
                                    data-shape={'round'}
                                    data-limit={'0'}
                                    data-more-button-type={'short'}
                                    data-services={
                                        'vkontakte,odnoklassniki,telegram,viber,whatsapp,moimir,lj'
                                    }
                                />
                            </div>
                        </div>
                        <h1 className={styles.title}>{article.title}</h1>
                        {coverImage ? (
                            <Image
                                className={styles.cover}
                                width={1300}
                                height={400}
                                src={coverImage}
                                alt={article.title}
                                draggable={false}
                                quality={100}
                                placeholder={generateImagePlaceHolder(
                                    1300,
                                    400
                                )}
                                loading={'eager'}
                            />
                        ) : null}
                    </header>
                    <div className={styles.content}>
                        {guardOutputData(article.content)
                            ? article.content.blocks.map(
                                  (block, index, data) => (
                                      <ArticleBlockItem
                                          key={block.id}
                                          item={block}
                                          index={index}
                                          data={data}
                                      />
                                  )
                              )
                            : null}
                    </div>
                </article>
                <ToTelegramBanner />
            </div>
            <ArticleGoogleSchema
                title={article.metaTitle || article.title}
                description={
                    article.metaDescription || article.excerpt || undefined
                }
                image={coverImage}
                createdAt={publishedAt}
                updatedAt={article.updatedAt.toISOString()}
                locale={locale}
                url={articleUrl}
                faq={
                    guardOutputData(article.content)
                        ? extractFaq(article.content)
                        : []
                }
            />
        </>
    )
}
